import { useDashboardMetrics } from '@/hooks/useDashboardMetrics';
import { useObrasDashboardMetrics } from '@/hooks/useObrasDashboardMetrics';
import { useRealtimeNotifications } from '@/hooks/useRealtimeNotifications';
import { useDashboardNavigation } from '@/hooks/useDashboardNavigation';
import { useOrdenesTrabajoContext } from '@/contexts/OrdenesTrabajoContext';
import { DashboardCard } from './DashboardCard';
import { ObrasFinalizadasSinDocsCard } from './ObrasFinalizadasSinDocsCard';
import { ActivityFeed } from './ActivityFeed';
import { OrdenesStatusCard } from './OrdenesStatusCard';
import { SystemsByTypeChart } from './SystemsByTypeChart';
import { Users, Monitor, Plus, CheckSquare, Building2, Clock, AlertTriangle, TrendingUp } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { tipoTrabajoLabels, getWorkTypeColor } from '@/lib/workTypeConstants';
import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useNavigate } from 'react-router-dom';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

export const UnifiedAdminDashboard = () => {
  const { metrics: systemMetrics, loading: systemLoading } = useDashboardMetrics();
  const { metrics: obrasMetrics, loading: obrasLoading } = useObrasDashboardMetrics();
  const { ordenes } = useOrdenesTrabajoContext();
  const { navigateToClients, scrollToObrasTab } = useDashboardNavigation();
  const navigate = useNavigate();
  const [selectedWorkType, setSelectedWorkType] = useState<string | null>(null);
  const [showWorkTypeModal, setShowWorkTypeModal] = useState(false);
  const [topMetric, setTopMetric] = useState<'horas' | 'coste' | 'ambos'>('horas');
  useRealtimeNotifications();

  if (systemLoading || obrasLoading) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {[...Array(8)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  // Prepare chart data for work types
  const workTypeChartData = Object.entries(obrasMetrics?.distribucion_tipos || {}).map(([tipo, data]) => ({
    name: tipoTrabajoLabels[tipo as keyof typeof tipoTrabajoLabels] || tipo,
    value: data.horas,
    percentage: Math.round(data.porcentaje)
  }));

  // Top 5 obras activas ordenadas por horas consumidas (descendente)
  const obrasActivasList = (obrasMetrics?.obras_detalle || []).filter(o => o.estado === 'activa');
  const topObrasData = [...obrasActivasList]
    .sort((a, b) => b.horas_consumidas - a.horas_consumidas)
    .slice(0, 5)
    .map(obra => ({
      nombre: obra.nombre.length > 15 ? obra.nombre.substring(0, 15) + '...' : obra.nombre,
      fullName: obra.nombre,
      obraId: obra.id,
      horas_estimadas: obra.horas_estimadas,
      horas_consumidas: obra.horas_consumidas,
      coste_estimado: obra.coste_estimado,
      coste_consumido: obra.coste_consumido,
    }));

  const handleWorkTypeClick = (workType: string) => {
    setSelectedWorkType(workType);
    setShowWorkTypeModal(true);
  };

  const handleObraClick = (obraId: string) => {
    navigate(`/obras?obra=${obraId}`);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Dashboard Administrativo</h2>
        <p className="text-muted-foreground">
          Resumen general del sistema y gestión de obras
        </p>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="general">Resumen General</TabsTrigger>
          <TabsTrigger value="obras">Gestión de Obras</TabsTrigger>
          <TabsTrigger value="actividad">Actividad y Sistemas</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-6">
          <ObrasFinalizadasSinDocsCard />
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <DashboardCard
              title="Total Clientes"
              value={systemMetrics.totalClientes}
              icon={Users}
              onClick={navigateToClients}
              className="cursor-pointer hover:bg-accent/50 transition-colors"
            />
            <DashboardCard
              title="Sistemas Instalados"
              value={systemMetrics.totalSistemas}
              icon={Monitor}
            />
            <DashboardCard
              title="Nuevas Altas Hoy"
              value={systemMetrics.nuevasAltasHoy}
              icon={Plus}
            />
            <DashboardCard
              title="Checklists Hoy"
              value={systemMetrics.checklistsHoy}
              icon={CheckSquare}
            />
          </div>

          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <DashboardCard
              title="Obras Activas"
              value={obrasMetrics?.obras_activas || 0}
              icon={Building2}
              onClick={scrollToObrasTab}
              className="cursor-pointer hover:bg-accent/50 transition-colors"
            />
            <DashboardCard
              title="Horas Consumidas"
              value={obrasMetrics?.horas_totales_consumidas || 0}
              icon={Clock}
            />
            <DashboardCard
              title="Horas Extra"
              value={parseFloat((obrasMetrics?.horas_extra_totales || 0).toFixed(1))}
              icon={AlertTriangle}
              className="border-orange-200 bg-orange-50/50 cursor-pointer hover:bg-orange-100/50 transition-colors"
              onClick={() => navigate('/jornadas-equipo')}
            />
            <DashboardCard
              title="Obras Sobrecargadas"
              value={obrasMetrics?.obras_sobrecargadas || 0}
              icon={AlertTriangle}
            />
            <DashboardCard
              title="Total Obras"
              value={obrasMetrics?.obras_detalle.length || 0}
              icon={TrendingUp}
            />
          </div>

          {workTypeChartData.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Distribución de Horas por Tipo de Trabajo</CardTitle>
                <CardDescription>
                  Porcentaje de horas trabajadas por tipo de intervención
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                       <Pie
                         data={workTypeChartData}
                         cx="50%"
                         cy="50%"
                         labelLine={false}
                         outerRadius={100}
                         innerRadius={50}
                         paddingAngle={2}
                         fill="#8884d8"
                         dataKey="value"
                         onClick={(data) => handleWorkTypeClick(data.name)}
                         className="cursor-pointer"
                       >
                         {workTypeChartData.map((_, index) => (
                           <Cell 
                             key={`cell-${index}`} 
                             fill={COLORS[index % COLORS.length]}
                             className="hover:opacity-80 transition-opacity"
                           />
                         ))}
                       </Pie>
                      <Tooltip formatter={(value: number, _n, p: any) => [`${value.toFixed(1)} h (${p.payload.percentage}%)`, p.payload.name]} />
                      <Legend verticalAlign="bottom" height={36} formatter={(value, entry: any) => `${value} — ${entry.payload.percentage}%`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="obras" className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <DashboardCard
              title="Obras Activas"
              value={obrasMetrics?.obras_activas || 0}
              icon={Building2}
              onClick={scrollToObrasTab}
              className="cursor-pointer hover:bg-accent/50 transition-colors"
            />
            <DashboardCard
              title="Horas Consumidas"
              value={obrasMetrics?.horas_totales_consumidas || 0}
              icon={Clock}
            />
            <DashboardCard
              title="Obras Sobrecargadas"
              value={obrasMetrics?.obras_sobrecargadas || 0}
              icon={AlertTriangle}
            />
            <DashboardCard
              title="Total Obras"
              value={obrasMetrics?.obras_detalle.length || 0}
              icon={TrendingUp}
            />
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            {workTypeChartData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Distribución por Tipo de Trabajo</CardTitle>
                  <CardDescription>Horas trabajadas por tipo</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                         <Pie
                           data={workTypeChartData}
                           cx="50%"
                           cy="50%"
                           outerRadius={70}
                           innerRadius={35}
                           paddingAngle={2}
                           fill="#8884d8"
                           dataKey="value"
                           onClick={(data) => handleWorkTypeClick(data.name)}
                           className="cursor-pointer"
                         >
                           {workTypeChartData.map((_, index) => (
                             <Cell 
                               key={`cell-${index}`} 
                               fill={COLORS[index % COLORS.length]}
                               className="hover:opacity-80 transition-opacity"
                             />
                           ))}
                         </Pie>
                        <Tooltip formatter={(value: number, _n, p: any) => [`${value.toFixed(1)} h (${p.payload.percentage}%)`, p.payload.name]} />
                        <Legend verticalAlign="bottom" height={36} formatter={(value, entry: any) => `${value} — ${entry.payload.percentage}%`} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}

            {topObrasData.length > 0 && (
              <Card>
                <CardHeader>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <CardTitle>Top 5 Obras — Estimadas vs Consumidas</CardTitle>
                      <CardDescription>Comparación de progreso por obra</CardDescription>
                    </div>
                    <div className="inline-flex rounded-md border bg-muted/40 p-0.5">
                      {(['horas','coste','ambos'] as const).map(m => (
                        <Button
                          key={m}
                          type="button"
                          size="sm"
                          variant={topMetric === m ? 'default' : 'ghost'}
                          className="h-7 px-3 text-xs"
                          onClick={() => setTopMetric(m)}
                        >
                          {m === 'horas' ? 'Horas' : m === 'coste' ? '€' : 'Ambos'}
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="h-72">
                     <ResponsiveContainer width="100%" height="100%">
                       <BarChart data={topObrasData}>
                         <CartesianGrid strokeDasharray="3 3" />
                         <XAxis dataKey="nombre" />
                         {(topMetric === 'horas' || topMetric === 'ambos') && (
                           <YAxis yAxisId="h" orientation="left" tickFormatter={(v) => `${v}h`} />
                         )}
                         {(topMetric === 'coste' || topMetric === 'ambos') && (
                           <YAxis yAxisId="c" orientation={topMetric === 'ambos' ? 'right' : 'left'} tickFormatter={(v) => `${Math.round(Number(v))}€`} />
                         )}
                         <Tooltip 
                           labelFormatter={(label, payload) => {
                             const data = payload?.[0]?.payload;
                             return data?.fullName || label;
                           }}
                           formatter={(value: any, name: string) => {
                             const num = Number(value);
                             if (name.includes('€')) return [`${num.toFixed(2)} €`, name];
                             return [`${num.toFixed(1)} h`, name];
                           }}
                         />
                         <Legend />
                         {(topMetric === 'horas' || topMetric === 'ambos') && (
                           <>
                             <Bar yAxisId="h" dataKey="horas_estimadas" fill="#8884d8" name="Horas estimadas"
                               onClick={(d: any) => handleObraClick(d.obraId)} className="cursor-pointer hover:opacity-80" />
                             <Bar yAxisId="h" dataKey="horas_consumidas" fill="#82ca9d" name="Horas consumidas"
                               onClick={(d: any) => handleObraClick(d.obraId)} className="cursor-pointer hover:opacity-80" />
                           </>
                         )}
                         {(topMetric === 'coste' || topMetric === 'ambos') && (
                           <>
                             <Bar yAxisId="c" dataKey="coste_estimado" fill="#fbbf24" name="Coste estimado €"
                               onClick={(d: any) => handleObraClick(d.obraId)} className="cursor-pointer hover:opacity-80" />
                             <Bar yAxisId="c" dataKey="coste_consumido" fill="#f97316" name="Coste consumido €"
                               onClick={(d: any) => handleObraClick(d.obraId)} className="cursor-pointer hover:opacity-80" />
                           </>
                         )}
                       </BarChart>
                     </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Resumen de Obras Activas</CardTitle>
              <CardDescription>Estado actual de todas las obras en progreso</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {obrasActivasList.slice(0, 10).map((obra) => (
                  <div 
                    key={obra.id} 
                    className="flex items-center justify-between p-4 border rounded-lg cursor-pointer hover:bg-accent/50 transition-colors"
                    onClick={() => handleObraClick(obra.id)}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-medium">{obra.nombre}</h4>
                        <Badge variant={obra.estado === 'activa' ? 'default' : 'secondary'}>
                          {obra.estado}
                        </Badge>
                        {obra.sobrecarga && (
                          <Badge variant="destructive">Sobrecargada</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{obra.cliente.nombre}</p>
                      <div className="mt-2">
                        <div className="flex justify-between text-sm mb-1">
                          <span>Progreso: {Math.round(obra.progreso)}%</span>
                          <span>{obra.horas_consumidas}h / {obra.horas_estimadas}h</span>
                        </div>
                        <Progress value={obra.progreso} className="h-2" />
                      </div>
                    </div>
                    <div className="ml-4 text-right text-sm">
                      <div className="font-medium">{obra.tecnicos_asignados.length} técnicos</div>
                      <div className="text-muted-foreground">
                        {Object.entries(obra.jornadas_por_tipo)
                          .filter(([, data]) => data.horas > 0)
                          .map(([tipo]) => tipoTrabajoLabels[tipo as keyof typeof tipoTrabajoLabels] || tipo)
                          .join(', ')}
                      </div>
                    </div>
                  </div>
                ))}
                {obrasActivasList.length === 0 && (
                  <p className="text-sm text-muted-foreground italic text-center py-6">
                    No hay obras activas en este momento.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="actividad" className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <SystemsByTypeChart data={systemMetrics.sistemasPorTipo} />
            <OrdenesStatusCard ordenes={ordenes} />
          </div>
          
          <div className="grid gap-4 md:grid-cols-7">
            <ActivityFeed activities={systemMetrics.actividadReciente} />
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={showWorkTypeModal} onOpenChange={setShowWorkTypeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Detalles de {selectedWorkType}</DialogTitle>
            <DialogDescription>
              Información detallada sobre las jornadas de tipo {selectedWorkType}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {obrasMetrics?.obras_detalle
              .filter(obra => 
                Object.keys(obra.jornadas_por_tipo).some(tipo => 
                  tipoTrabajoLabels[tipo as keyof typeof tipoTrabajoLabels] === selectedWorkType && 
                  obra.jornadas_por_tipo[tipo].horas > 0
                )
              )
              .map(obra => (
                <div key={obra.id} className="p-3 border rounded-lg">
                  <div className="font-medium">{obra.nombre}</div>
                  <div className="text-sm text-muted-foreground">{obra.cliente.nombre}</div>
                  <div className="text-sm">
                    {Object.entries(obra.jornadas_por_tipo)
                      .filter(([tipo, data]) => 
                        tipoTrabajoLabels[tipo as keyof typeof tipoTrabajoLabels] === selectedWorkType && 
                        data.horas > 0
                      )
                      .map(([, data]) => `${data.horas} horas, ${data.cantidad} jornadas`)
                      .join(', ')}
                  </div>
                </div>
              ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};